/**
 * Todos feature — Ducks pattern (actions, reducer, types).
 * Owns the full Redux lifecycle for todo state.
 */

import { Todo } from '@/infra/types/domain';
import { SmartTodoResult } from '@/infra/types/api';

// ─── State Shape ───────────────────────────────────────────

export interface TodoFilters {
  status: string;       // 'all' | TodoStatus
  owner: string;        // 'all' | user_id
  source: string;       // 'all' | source_plugin
  close_reason: string; // 'all' | 'completed' | 'vanished'
}

export interface TodosState {
  byId: Record<string, Todo>;
  allIds: string[];
  filters: TodoFilters;
  loading: boolean;
  error: string | null;
  lastSyncAt: number | null;
  smartLoading: boolean;
  smartError: string | null;
  lastSmartTodo: Todo | null;
  lastSmartResult: SmartTodoResult | null;
}

export const initialState: TodosState = {
  byId: {},
  allIds: [],
  filters: {
    status: 'all',
    owner: 'all',
    source: 'all',
    close_reason: 'all',
  },
  loading: false,
  error: null,
  lastSyncAt: null,
  smartLoading: false,
  smartError: null,
  lastSmartTodo: null,
  lastSmartResult: null,
};

// ─── Action Types ──────────────────────────────────────────

const SET_TODOS = 'todos/setTodos';
const ADD_TODO = 'todos/addTodo';
const UPDATE_TODO = 'todos/updateTodo';
// Exported so cross-slice reducers (e.g. the projects inbox-outlines store)
// can listen for a server-truth Todo replace and patch their own copy of the
// node — a schedule-move yields replaceTodo, which flips the Planned badge in
// the project hierarchy without a refetch.
export const REPLACE_TODO = 'todos/replaceTodo';
const REMOVE_TODO = 'todos/removeTodo';
const SET_FILTERS = 'todos/setFilters';
const SET_LOADING = 'todos/setLoading';
const SET_ERROR = 'todos/setError';
const SET_SMART_LOADING = 'todos/setSmartLoading';
const SET_SMART_ERROR = 'todos/setSmartError';
const SET_LAST_SMART_TODO = 'todos/setLastSmartTodo';
const SET_LAST_SMART_RESULT = 'todos/setLastSmartResult';

// Trigger actions (saga watchers)
export const FETCH_TODOS = 'todos/fetchTodos';
export const CREATE_TODO = 'todos/createTodo';
export const SMART_CREATE_TODO = 'todos/smartCreateTodo';
export const UPDATE_TODO_REQUESTED = 'todos/updateTodoRequested';
export const DELETE_TODO = 'todos/deleteTodo';
export const SCHEDULE_COMPLETE_REQUESTED = 'todos/scheduleCompleteRequested';
export const CANCEL_SCHEDULED_COMPLETE_REQUESTED = 'todos/cancelScheduledCompleteRequested';
export const CLAIM_TODO_REQUESTED = 'todos/claimTodoRequested';
export const SKIP_TODO_REQUESTED = 'todos/skipTodoRequested';
export const MOVE_TODO_REQUESTED = 'todos/moveTodoRequested';
export const MARK_BLOCKED_REQUESTED = 'todos/markBlockedRequested';
export const UNBLOCK_TODO_REQUESTED = 'todos/unblockTodoRequested';

// ─── Action Creators ───────────────────────────────────────

export function setTodos(todos: Todo[]) {
  return { type: SET_TODOS, payload: todos } as const;
}

export function addTodo(todo: Todo) {
  return { type: ADD_TODO, payload: todo } as const;
}

export function updateTodo(id: string, todo: Partial<Todo>) {
  return { type: UPDATE_TODO, payload: { id, todo } } as const;
}

// Use when you have the full authoritative Todo object (HTTP PATCH response,
// WS full_object). Merging via UPDATE_TODO leaves stale values for fields
// the server omitted — proto3 `optional` Timestamps like due_at aren't
// emitted even with EmitUnpopulated=true, so a cleared due_at never
// overwrites the old one in state. REPLACE_TODO sets byId[id] outright.
export function replaceTodo(todo: Todo) {
  return { type: REPLACE_TODO, payload: todo } as const;
}

export function removeTodo(id: string) {
  return { type: REMOVE_TODO, payload: id } as const;
}

export function setFilters(filters: Partial<TodoFilters>) {
  return { type: SET_FILTERS, payload: filters } as const;
}

export function setLoading(loading: boolean) {
  return { type: SET_LOADING, payload: loading } as const;
}

export function setError(error: string | null) {
  return { type: SET_ERROR, payload: error } as const;
}

export function setSmartLoading(loading: boolean) {
  return { type: SET_SMART_LOADING, payload: loading } as const;
}

export function setSmartError(error: string | null) {
  return { type: SET_SMART_ERROR, payload: error } as const;
}

export function setLastSmartTodo(todo: Todo | null) {
  return { type: SET_LAST_SMART_TODO, payload: todo } as const;
}

export function setLastSmartResult(result: SmartTodoResult | null) {
  return { type: SET_LAST_SMART_RESULT, payload: result } as const;
}

// Trigger action creators
export function fetchTodos() {
  return { type: FETCH_TODOS } as const;
}

export function createTodoAction(payload: { title: string; owner: string; type_tag: string }) {
  return { type: CREATE_TODO, payload } as const;
}

export function updateTodoRequested(id: string, changes: Partial<Todo>) {
  return { type: UPDATE_TODO_REQUESTED, payload: { id, changes } } as const;
}

export function smartCreateTodoAction(text: string) {
  return { type: SMART_CREATE_TODO, payload: { text } } as const;
}

export function deleteTodoAction(id: string) {
  return { type: DELETE_TODO, payload: id } as const;
}

export function scheduleCompleteRequested(id: string) {
  return { type: SCHEDULE_COMPLETE_REQUESTED, payload: { id } } as const;
}

export function cancelScheduledCompleteRequested(id: string) {
  return { type: CANCEL_SCHEDULED_COMPLETE_REQUESTED, payload: { id } } as const;
}

export function claimTodoRequested(id: string, newOwnerId: string) {
  return { type: CLAIM_TODO_REQUESTED, payload: { id, newOwnerId } } as const;
}

export function skipTodoRequested(id: string) {
  return { type: SKIP_TODO_REQUESTED, payload: { id } } as const;
}

export function moveTodoRequested(id: string, bucket: 'today' | 'inbox' | 'tomorrow') {
  return { type: MOVE_TODO_REQUESTED, payload: { id, bucket } } as const;
}

// Mark Blocked — closes the original (close_reason="blocked") and creates a
// follow-up todo N working days out. Server is authoritative; the saga
// reads the response and adds the follow-up to the store. See
// markBlockedTodoSaga for the wire interaction.
export function markBlockedRequested(id: string) {
  return { type: MARK_BLOCKED_REQUESTED, payload: { id } } as const;
}

// Unblock — reverses a prior "blocked" completion. The server tombstones
// the follow-up todo and clears close_reason on the original; the saga
// just fires the RPC, dispatches a refetch, and shows an error toast on
// failure. Idempotent server-side: hitting Unblock twice is harmless.
export function unblockTodoRequested(id: string) {
  return { type: UNBLOCK_TODO_REQUESTED, payload: { id } } as const;
}

// ─── Action Type Union ─────────────────────────────────────

type TodosAction =
  | ReturnType<typeof setTodos>
  | ReturnType<typeof addTodo>
  | ReturnType<typeof updateTodo>
  | ReturnType<typeof replaceTodo>
  | ReturnType<typeof removeTodo>
  | ReturnType<typeof setFilters>
  | ReturnType<typeof setLoading>
  | ReturnType<typeof setError>
  | ReturnType<typeof setSmartLoading>
  | ReturnType<typeof setSmartError>
  | ReturnType<typeof setLastSmartTodo>
  | ReturnType<typeof setLastSmartResult>;

// ─── Reducer ───────────────────────────────────────────────

export function todosReducer(
  state: TodosState = initialState,
  action: TodosAction | { type: string; payload?: unknown }
): TodosState {
  switch (action.type) {
    case SET_TODOS: {
      const todos = (action as ReturnType<typeof setTodos>).payload;
      const byId: Record<string, Todo> = {};
      const allIds: string[] = [];
      for (const todo of todos) {
        byId[todo.id] = todo;
        allIds.push(todo.id);
      }
      return {
        ...state,
        byId,
        allIds,
        lastSyncAt: Date.now(),
      };
    }

    case ADD_TODO: {
      const todo = (action as ReturnType<typeof addTodo>).payload;
      // Idempotent: smart-create dispatches addTodo locally AND the server
      // fans the same creation back via WS, so both hit this reducer. Dedupe
      // on id so the list doesn't show two rows for one todo.
      const alreadyPresent = state.byId[todo.id] !== undefined;
      return {
        ...state,
        byId: { ...state.byId, [todo.id]: todo },
        allIds: alreadyPresent ? state.allIds : [...state.allIds, todo.id],
      };
    }

    case UPDATE_TODO: {
      const { id, todo } = (action as ReturnType<typeof updateTodo>).payload;
      if (!state.byId[id]) {
        return state;
      }
      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: { ...state.byId[id], ...todo },
        },
      };
    }

    case REPLACE_TODO: {
      const todo = (action as ReturnType<typeof replaceTodo>).payload;
      const alreadyPresent = state.byId[todo.id] !== undefined;
      return {
        ...state,
        byId: { ...state.byId, [todo.id]: todo },
        allIds: alreadyPresent ? state.allIds : [...state.allIds, todo.id],
      };
    }

    case REMOVE_TODO: {
      const id = (action as ReturnType<typeof removeTodo>).payload;
      if (!state.byId[id]) {
        return state;
      }
      const { [id]: _removed, ...restById } = state.byId;
      return {
        ...state,
        byId: restById,
        allIds: state.allIds.filter((i) => i !== id),
      };
    }

    case SET_FILTERS: {
      const filters = (action as ReturnType<typeof setFilters>).payload;
      return {
        ...state,
        filters: { ...state.filters, ...filters },
      };
    }

    case SET_LOADING: {
      const loading = (action as ReturnType<typeof setLoading>).payload;
      return { ...state, loading };
    }

    case SET_ERROR: {
      const error = (action as ReturnType<typeof setError>).payload;
      return { ...state, error };
    }

    case SET_SMART_LOADING: {
      const smartLoading = (action as ReturnType<typeof setSmartLoading>).payload;
      return { ...state, smartLoading };
    }

    case SET_SMART_ERROR: {
      const smartError = (action as ReturnType<typeof setSmartError>).payload;
      return { ...state, smartError };
    }

    case SET_LAST_SMART_TODO: {
      const lastSmartTodo = (action as ReturnType<typeof setLastSmartTodo>).payload;
      return { ...state, lastSmartTodo };
    }

    case SET_LAST_SMART_RESULT: {
      const lastSmartResult = (action as ReturnType<typeof setLastSmartResult>).payload;
      return { ...state, lastSmartResult };
    }

    default:
      return state;
  }
}
